import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import Home from './pages/Home';
import ParentShare from './pages/ParentShare';
import Toast from './components/Toast';
import Portal from './components/Portal';
// import InstallPrompt from './components/InstallPrompt';
import i18n from './i18n';
import { useAuthStore } from './store/useAuthStore';

const NETWORK_ERROR_TOAST_EVENT = 'app:network-error-toast';

interface NetworkToastDetail {
  id?: number;
  message: string;
  type?: 'success' | 'error';
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((state) => state.token);
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((state) => state.token);
  if (token) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function App() {
  const [networkToast, setNetworkToast] = useState<NetworkToastDetail | null>(null);

  useEffect(() => {
    const handleNetworkErrorToast = (event: Event) => {
      const customEvent = event as CustomEvent<NetworkToastDetail>;
      if (!customEvent.detail?.message) {
        setNetworkToast(null);
        return;
      }

      setNetworkToast({
        id: customEvent.detail.id ?? Date.now(),
        message: customEvent.detail.message,
        type: customEvent.detail.type ?? 'error',
      });
    };

    const showBrowserNetworkToast = (message: string, type: 'success' | 'error') => {
      setNetworkToast({
        id: Date.now(),
        message,
        type,
      });
    };

    const handleOffline = () => {
      showBrowserNetworkToast(i18n.t('common.networkError'), 'error');
    };

    const handleOnline = () => {
      showBrowserNetworkToast(i18n.t('common.networkRestored'), 'success');
    };

    window.addEventListener(NETWORK_ERROR_TOAST_EVENT, handleNetworkErrorToast);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener(NETWORK_ERROR_TOAST_EVENT, handleNetworkErrorToast);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  return (
    <Router>
      {networkToast ? (
        <Portal>
          <Toast
            key={networkToast.id}
            message={networkToast.message}
            type={networkToast.type ?? 'error'}
            onClose={() => setNetworkToast(null)}
          />
        </Portal>
      ) : null}
      <Routes>
        <Route path="/share/:token" element={<ParentShare />} />
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        {/* <Route
          path="/class"
          element={
            <ProtectedRoute>
              <ClassManagement />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pet"
          element={
            <ProtectedRoute>
              <PetCenter />
            </ProtectedRoute>
          }
        />
        <Route
          path="/store"
          element={
            <ProtectedRoute>
              <Store />
            </ProtectedRoute>
          }
        />
        <Route
          path="/rank"
          element={
            <ProtectedRoute>
              <Rank />
            </ProtectedRoute>
          }
        /> */}
      </Routes>
      {/* <InstallPrompt /> */}
    </Router>
  );
}

export default App;
